import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Building2, Plus, Search, RefreshCcw, Edit, Trash2, Eye,
  Users, GraduationCap, BookOpen, FolderOpen, UserCircle,
  CheckCircle, XCircle, Pause, Play, Mail, ChevronRight,
  Filter, X, AlertTriangle, Globe, Phone, MapPin, Shield
} from 'lucide-react';

const CentersManagementPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [centers, setCenters] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    siret: '',
    nda: '',
    website: '',
    subscription_plan_id: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: ''
  });
  
  const [suspendReason, setSuspendReason] = useState('Retard de paiement');

  const fetchCenters = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPlan !== 'all') params.append('plan_id', filterPlan);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get(`/super-admin/centers?${params}`);
      setCenters(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des centres');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPlan, searchTerm]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await api.get('/super-admin/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  }, []);

  useEffect(() => {
    fetchCenters();
    fetchPlans();
    
    // Check if we should open create dialog
    if (searchParams.get('action') === 'create') {
      setShowCreateDialog(true);
      // Replace (not push) so the Back button doesn't reopen this dialog in a loop.
      setSearchParams({}, { replace: true });
    }
  }, [fetchCenters, fetchPlans, searchParams, setSearchParams]);

  // Helper function to translate Pydantic error messages to French
  const translateError = (error) => {
    const fieldTranslations = {
      'name': 'Nom du centre',
      'email': 'Email du centre',
      'admin_email': 'Email de l\'administrateur',
      'admin_first_name': 'Prénom de l\'administrateur',
      'admin_last_name': 'Nom de l\'administrateur',
      'phone': 'Téléphone',
      'siret': 'SIRET',
      'city': 'Ville'
    };

    const messageTranslations = {
      'value is not a valid email address': 'L\'adresse email n\'est pas valide',
      'An email address must have an @-sign': 'L\'adresse email doit contenir un @',
      'field required': 'Ce champ est obligatoire',
      'string_too_short': 'Le texte est trop court',
      'string_too_long': 'Le texte est trop long',
      'value_error': 'Valeur invalide'
    };

    let msg = error.msg || error.message || 'Erreur de validation';
    let field = error.loc ? error.loc[error.loc.length - 1] : '';
    
    // Translate field name
    const fieldName = fieldTranslations[field] || field;
    
    // Translate message
    for (const [en, fr] of Object.entries(messageTranslations)) {
      if (msg.includes(en)) {
        msg = msg.replace(en, fr);
      }
    }

    return fieldName ? `${fieldName}: ${msg}` : msg;
  };

  // Helper function to validate email format
  const isValidEmail = (email) => {
    if (!email || !email.trim()) return true; // Empty or whitespace is ok for optional fields
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const handleCreate = async () => {
    // Validation with French messages
    if (!formData.name?.trim()) {
      toast.error('Le nom du centre est obligatoire');
      return;
    }
    if (!formData.admin_first_name?.trim()) {
      toast.error('Le prénom de l\'administrateur est obligatoire');
      return;
    }
    if (!formData.admin_last_name?.trim()) {
      toast.error('Le nom de l\'administrateur est obligatoire');
      return;
    }
    if (!formData.admin_email?.trim()) {
      toast.error('L\'email de l\'administrateur est obligatoire');
      return;
    }
    if (!isValidEmail(formData.admin_email?.trim())) {
      toast.error('L\'email de l\'administrateur n\'est pas valide (doit contenir @)');
      return;
    }
    // Only validate center email if it's not empty/whitespace
    const centerEmail = formData.email?.trim();
    if (centerEmail && !isValidEmail(centerEmail)) {
      toast.error('L\'email du centre n\'est pas valide (doit contenir @)');
      return;
    }
    
    try {
      // Clean the data - convert empty strings to null for optional fields
      const cleanedData = {
        name: formData.name.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        postal_code: formData.postal_code?.trim() || null,
        country: formData.country?.trim() || 'France',
        siret: formData.siret?.trim() || null,
        nda: formData.nda?.trim() || null,
        website: formData.website?.trim() || null,
        subscription_plan_id: formData.subscription_plan_id ? parseInt(formData.subscription_plan_id) : null,
        admin_first_name: formData.admin_first_name.trim(),
        admin_last_name: formData.admin_last_name.trim(),
        admin_email: formData.admin_email.trim()
      };
      
      const response = await api.post('/super-admin/centers', cleanedData);
      toast.success('Centre créé avec succès');
      toast.info('Email d\'activation envoyé à l\'administrateur');
      setShowCreateDialog(false);
      resetForm();
      fetchCenters();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or simple string errors
      const errorData = error.response?.data?.detail;
      let errorMessage = 'Erreur lors de la création du centre';
      
      if (typeof errorData === 'string') {
        // Check for common English messages and translate
        if (errorData.includes('email')) {
          errorMessage = 'Erreur: Adresse email invalide';
        } else {
          errorMessage = errorData;
        }
      } else if (Array.isArray(errorData)) {
        // Pydantic validation errors come as an array - translate each one
        errorMessage = errorData.map(err => translateError(err)).join('\n');
      } else if (errorData && typeof errorData === 'object') {
        errorMessage = translateError(errorData);
      }
      
      toast.error(errorMessage);
    }
  };

  const handleUpdate = async () => {
    if (!selectedCenter) return;
    
    // Validation with French messages
    if (!formData.name?.trim()) {
      toast.error('Le nom du centre est obligatoire');
      return;
    }
    
    // Only validate center email if it's not empty/whitespace
    const centerEmail = formData.email?.trim();
    if (centerEmail && !isValidEmail(centerEmail)) {
      toast.error('L\'email du centre n\'est pas valide (doit contenir @)');
      return;
    }
    
    try {
      // Clean the data - convert empty strings to null for optional fields
      const cleanedData = {
        name: formData.name.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        postal_code: formData.postal_code?.trim() || null,
        country: formData.country?.trim() || 'France',
        siret: formData.siret?.trim() || null,
        nda: formData.nda?.trim() || null,
        website: formData.website?.trim() || null,
        subscription_plan_id: formData.subscription_plan_id ? parseInt(formData.subscription_plan_id) : null
      };
      
      await api.put(`/super-admin/centers/${selectedCenter.id}`, cleanedData);
      toast.success('Centre mis à jour avec succès');
      setShowEditDialog(false);
      fetchCenters();
    } catch (error) {
      // Handle Pydantic validation errors
      const errorData = error.response?.data?.detail;
      let errorMessage = 'Erreur lors de la mise à jour';
      
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (Array.isArray(errorData)) {
        errorMessage = errorData.map(err => translateError(err)).join('\n');
      } else if (errorData && typeof errorData === 'object') {
        errorMessage = translateError(errorData);
      }
      
      toast.error(errorMessage);
    }
  };

  const handleSuspend = async () => {
    if (!selectedCenter) return;
    
    try {
      // Send reason in request body as JSON
      const response = await api.post(`/super-admin/centers/${selectedCenter.id}/suspend`, {
        reason: suspendReason || 'Retard de paiement'
      });
      
      const affectedUsers = response.data?.affected_users || 0;
      toast.success(`Centre "${selectedCenter.name}" suspendu`);
      toast.info(`${affectedUsers} utilisateur(s) n'auront plus accès à la plateforme`);
      
      setShowSuspendDialog(false);
      setSuspendReason('Retard de paiement');
      fetchCenters();
    } catch (error) {
      toast.error('Erreur lors de la suspension');
    }
  };

  const handleActivate = async (center) => {
    try {
      const response = await api.post(`/super-admin/centers/${center.id}/activate`);
      const restoredUsers = response.data?.restored_users || 0;
      toast.success(`Centre "${center.name}" réactivé`);
      toast.info(`${restoredUsers} utilisateur(s) ont retrouvé l'accès à la plateforme`);
      fetchCenters();
    } catch (error) {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const handleDelete = async () => {
    if (!selectedCenter) return;
    
    try {
      await api.delete(`/super-admin/centers/${selectedCenter.id}`);
      toast.success('Centre supprimé');
      setShowDeleteDialog(false);
      fetchCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEdit = (center) => {
    setSelectedCenter(center);
    setFormData({
      name: center.name || '',
      email: center.email || '',
      phone: center.phone || '',
      address: center.address || '',
      city: center.city || '',
      postal_code: center.postal_code || '',
      country: center.country || 'France',
      siret: center.siret || '',
      nda: center.nda || '',
      website: center.website || '',
      subscription_plan_id: center.subscription_plan?.id?.toString() || '',
      admin_first_name: '',
      admin_last_name: '',
      admin_email: ''
    });
    setShowEditDialog(true);
  };

  const openSuspend = (center) => {
    setSelectedCenter(center);
    setShowSuspendDialog(true);
  };

  const openDelete = (center) => {
    setSelectedCenter(center);
    setShowDeleteDialog(true);
  };

  const openDetails = (center) => {
    setSelectedCenter(center);
    setShowDetailsDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'France',
      siret: '',
      nda: '',
      website: '',
      subscription_plan_id: '',
      admin_first_name: '',
      admin_last_name: '',
      admin_email: ''
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Actif', variant: 'default', className: 'bg-emerald-100 text-emerald-700' },
      suspended: { label: 'Suspendu', variant: 'destructive', className: 'bg-red-100 text-red-700' },
      trial: { label: 'Essai', variant: 'secondary', className: 'bg-blue-100 text-blue-700' },
      expired: { label: 'Expiré', variant: 'outline', className: 'bg-slate-100 text-slate-700' }
    };
    const config = statusConfig[status] || statusConfig.trial;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6 p-6" data-testid="centers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onMenuClick} className="md:hidden p-2 -ml-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800" aria-label="Ouvrir le menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Gestion des Centres
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {centers.length} centre(s) sur la plateforme
            </p>
          </div>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="create-center-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Centre
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, ville, SIRET..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="trial">Essai</SelectItem>
                <SelectItem value="suspended">Suspendu</SelectItem>
                <SelectItem value="expired">Expiré</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPlan} onValueChange={setFilterPlan}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les plans</SelectItem>
                {plans.map(plan => (
                  <SelectItem key={plan.id} value={plan.id.toString()}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchCenters}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Centers List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : centers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Aucun centre trouvé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {centers.map(center => (
            <Card key={center.id} className="hover:shadow-md transition-shadow" data-testid={`center-${center.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Center Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-lg bg-violet-100 dark:bg-violet-900 flex items-center justify-center">
                        {center.logo_url ? (
                          <img src={center.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <Building2 className="w-6 h-6 text-violet-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white text-lg">
                          {center.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          {center.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {center.city}, {center.country}
                            </span>
                          )}
                          {center.siret && (
                            <span>• SIRET: {center.siret}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm">
                      {center.email && (
                        <span className="flex items-center gap-1 text-slate-600">
                          <Mail className="w-4 h-4" />
                          {center.email}
                        </span>
                      )}
                      {center.phone && (
                        <span className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-4 h-4" />
                          {center.phone}
                        </span>
                      )}
                      {center.website && (
                        <a 
                          href={center.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-violet-600 hover:underline"
                        >
                          <Globe className="w-4 h-4" />
                          Site web
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex gap-4 md:gap-6">
                    <div className="text-center">
                      <p className="text-xl font-bold text-slate-900 dark:text-white">
                        {center.counts?.students || 0}
                      </p>
                      <p className="text-xs text-slate-500">Étudiants</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-slate-900 dark:text-white">
                        {center.counts?.trainers || 0}
                      </p>
                      <p className="text-xs text-slate-500">Formateurs</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-slate-900 dark:text-white">
                        {center.counts?.courses || 0}
                      </p>
                      <p className="text-xs text-slate-500">Cours</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-slate-900 dark:text-white">
                        {center.counts?.admins || 0}
                      </p>
                      <p className="text-xs text-slate-500">Admins</p>
                    </div>
                  </div>

                  {/* Status & Plan */}
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(center.subscription_status)}
                    {center.subscription_plan && (
                      <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                        {center.subscription_plan.name}
                      </Badge>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openDetails(center)}
                      title="Voir détails"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openEdit(center)}
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {center.subscription_status === 'suspended' ? (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleActivate(center)}
                        title="Réactiver"
                        className="text-emerald-600 hover:bg-emerald-50"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openSuspend(center)}
                        title="Suspendre"
                        className="text-orange-600 hover:bg-orange-50"
                      >
                        <Pause className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDelete(center)}
                      title="Supprimer"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Suspension reason */}
                {center.subscription_status === 'suspended' && center.suspended_reason && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-700">Centre suspendu</p>
                      <p className="text-sm text-red-600">{center.suspended_reason}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-600" />
              Créer un nouveau centre
            </DialogTitle>
            <DialogDescription>
              Un compte administrateur sera créé automatiquement et recevra un email d'activation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Center Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Informations du centre
              </h4>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du centre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="CFA Excellence Formation"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email officiel (optionnel)</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="contact@centre.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="01 23 45 67 89"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Site web</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://www.centre.fr"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 rue de la Formation"
                />
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    placeholder="France"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SIRET</Label>
                  <Input
                    value={formData.siret}
                    onChange={(e) => setFormData({...formData, siret: e.target.value})}
                    placeholder="123 456 789 00012"
                  />
                </div>
                <div className="space-y-2">
                  <Label>NDA (Numéro de déclaration d'activité)</Label>
                  <Input
                    value={formData.nda}
                    onChange={(e) => setFormData({...formData, nda: e.target.value})}
                    placeholder="11 75 12345 75"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Plan d'abonnement</Label>
                <Select 
                  value={formData.subscription_plan_id} 
                  onValueChange={(v) => setFormData({...formData, subscription_plan_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(plan => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name} - {plan.price_monthly}€/mois
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Admin Info */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                <UserCircle className="w-4 h-4" />
                Administrateur CFA principal
              </h4>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    value={formData.admin_first_name}
                    onChange={(e) => setFormData({...formData, admin_first_name: e.target.value})}
                    placeholder="Jean"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={formData.admin_last_name}
                    onChange={(e) => setFormData({...formData, admin_last_name: e.target.value})}
                    placeholder="Dupont"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email de l'administrateur *</Label>
                <Input
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                  placeholder="admin@centre.fr"
                />
                <p className="text-xs text-slate-500">
                  Un email d'activation sera envoyé à cette adresse
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" />
              Créer le centre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le centre</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du centre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Site web</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
              />
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Pays</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input
                  value={formData.siret}
                  onChange={(e) => setFormData({...formData, siret: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>NDA</Label>
                <Input
                  value={formData.nda}
                  onChange={(e) => setFormData({...formData, nda: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Plan d'abonnement</Label>
              <Select 
                value={formData.subscription_plan_id} 
                onValueChange={(v) => setFormData({...formData, subscription_plan_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.name} - {plan.price_monthly}€/mois
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdate} className="bg-violet-600 hover:bg-violet-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Suspendre le centre
            </DialogTitle>
            <DialogDescription>
              Tous les utilisateurs de ce centre seront bloqués.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-slate-700 dark:text-slate-300">
              Êtes-vous sûr de vouloir suspendre <strong>{selectedCenter?.name}</strong> ?
            </p>
            
            {/* Warning box */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <p className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-2">
                Les utilisateurs suivants n'auront plus accès :
              </p>
              <ul className="text-sm text-orange-600 dark:text-orange-400 space-y-1 list-disc list-inside">
                <li>Administrateurs CFA ({selectedCenter?.counts?.admins || 0})</li>
                <li>Formateurs ({selectedCenter?.counts?.trainers || 0})</li>
                <li>Étudiants ({selectedCenter?.counts?.students || 0})</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label>Raison de la suspension</Label>
              <Select 
                value={suspendReason} 
                onValueChange={setSuspendReason}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une raison" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Retard de paiement">Retard de paiement</SelectItem>
                  <SelectItem value="Demande du centre">Demande du centre</SelectItem>
                  <SelectItem value="Non-conformité">Non-conformité</SelectItem>
                  <SelectItem value="Fin de contrat">Fin de contrat</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Ce motif sera affiché aux utilisateurs lors de leur tentative de connexion.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSuspend} className="bg-orange-600 hover:bg-orange-700">
              <Pause className="w-4 h-4 mr-2" />
              Suspendre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer le centre
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Toutes les données seront perdues.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-700 dark:text-slate-300">
              Êtes-vous sûr de vouloir supprimer définitivement <strong>{selectedCenter?.name}</strong> ?
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleDelete} variant="destructive">
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-600" />
              Détails du centre
            </DialogTitle>
          </DialogHeader>
          
          {selectedCenter && (
            <div className="space-y-6 py-4">
              {/* Header with status */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-xl font-semibold">{selectedCenter.name}</h3>
                  <p className="text-sm text-slate-500">{selectedCenter.email || 'Pas d\'email'}</p>
                </div>
                <Badge className={getStatusBadge(selectedCenter.subscription_status).className}>
                  {getStatusBadge(selectedCenter.subscription_status).label}
                </Badge>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-700">{selectedCenter.counts?.students || 0}</p>
                  <p className="text-xs text-blue-600">Étudiants</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                  <GraduationCap className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                  <p className="text-2xl font-bold text-purple-700">{selectedCenter.counts?.trainers || 0}</p>
                  <p className="text-xs text-purple-600">Formateurs</p>
                </div>
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                  <BookOpen className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
                  <p className="text-2xl font-bold text-emerald-700">{selectedCenter.counts?.courses || 0}</p>
                  <p className="text-xs text-emerald-600">Cours</p>
                </div>
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                  <Shield className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                  <p className="text-2xl font-bold text-amber-700">{selectedCenter.counts?.admins || 0}</p>
                  <p className="text-xs text-amber-600">Admins</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-700 dark:text-slate-300">Coordonnées</h4>
                  {selectedCenter.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{selectedCenter.phone}</span>
                    </div>
                  )}
                  {selectedCenter.address && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{selectedCenter.address}</span>
                    </div>
                  )}
                  {(selectedCenter.city || selectedCenter.postal_code) && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{selectedCenter.postal_code} {selectedCenter.city}</span>
                    </div>
                  )}
                  {selectedCenter.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <a href={selectedCenter.website} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
                        {selectedCenter.website}
                      </a>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-700 dark:text-slate-300">Informations légales</h4>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">SIRET:</span>
                    <span>{selectedCenter.siret || 'Non renseigné'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">NDA:</span>
                    <span>{selectedCenter.nda || 'Non renseigné'}</span>
                  </div>
                  {selectedCenter.subscription_plan && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-500">Plan:</span>
                      <Badge variant="outline">{selectedCenter.subscription_plan.name}</Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Suspension info */}
              {selectedCenter.subscription_status === 'suspended' && selectedCenter.suspended_reason && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-700 dark:text-red-400">Centre suspendu</p>
                      <p className="text-sm text-red-600 dark:text-red-300">Motif: {selectedCenter.suspended_reason}</p>
                      {selectedCenter.suspended_at && (
                        <p className="text-xs text-red-500 mt-1">
                          Depuis le {new Date(selectedCenter.suspended_at).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="flex justify-between text-xs text-slate-500 pt-4 border-t">
                <span>Créé le {new Date(selectedCenter.created_at).toLocaleDateString('fr-FR')}</span>
                {selectedCenter.updated_at && (
                  <span>Modifié le {new Date(selectedCenter.updated_at).toLocaleDateString('fr-FR')}</span>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Fermer
            </Button>
            <Button onClick={() => { setShowDetailsDialog(false); openEdit(selectedCenter); }}>
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CentersManagementPage;
